import React, { useState, useEffect } from "react";
import { Calendar, Clock, MessageCircle, Star, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCurrency } from "../contexts/CurrencyContext";
import PayPalCheckout, { PayDivider, isNativeApp } from "../components/PayPalCheckout";
import { useNativePayPal } from "../lib/paypalNative";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TIME_SLOTS = [
  "10:00 AM", "11:00 AM", "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM",
  "04:00 PM", "05:00 PM", "06:00 PM", "07:00 PM", "08:00 PM", "09:00 PM",
  "10:00 PM", "11:00 PM",
];
const CONCERNS = ["Marriage & Relationship", "Career & Finance", "Health & Wellness", "Foreign Settlement", "Education", "Business Timing", "General Life Reading"];

// "Consult Now" = book a slot starting in 5 minutes from now (15-min session)
function consultNowSlot() {
  const d = new Date(Date.now() + 5 * 60 * 1000);
  const hh = d.getHours();
  const mm = d.getMinutes();
  const period = hh >= 12 ? "PM" : "AM";
  const h12 = ((hh + 11) % 12) + 1;
  return {
    date: d.toISOString().split("T")[0],
    slot: `${String(h12).padStart(2, "0")}:${String(mm).padStart(2, "0")} ${period}`,
  };
}

export default function ConsultationPage() {
  const { user } = useAuth();
  const { isUSD, locked, price, priceOld, priceInr } = useCurrency();
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time_slot: "", concern: "", custom_concern: "", phone: "" });
  const [mode, setMode] = useState("instant"); // "instant" (chat now) | "scheduled"
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [waUrls, setWaUrls] = useState({ astrologer: "", customer: "", booking_ref: "" });
  const [chatId, setChatId] = useState("");
  const [availability, setAvailability] = useState({ online: true, reply_minutes: 10 });
  useEffect(() => {
    axios.get(`${API}/public/astrologer-availability`)
      .then(r => setAvailability(r.data))
      .catch(() => {});
  }, []);
  // When the user already has an active session, hide the booking form by default
  const activeConsult = user?.active_consultation || null;
  const [bookAnother, setBookAnother] = useState(false);
  const showActiveBanner = Boolean(activeConsult && !success);
  const showBookingForm = !showActiveBanner || bookAnother;

  // Allow same-day bookings up to midnight; max 30 days out.
  const todayISO = new Date().toISOString().split("T")[0];
  const minDate = todayISO;
  const maxDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  const handleConsultNow = () => {
    const slot = consultNowSlot();
    setForm(f => ({ ...f, date: slot.date, time_slot: slot.slot }));
    setTimeout(() => {
      document.querySelector('[data-testid="book-consultation-submit-btn"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  // Filter time slots: hide any slot in the past when the chosen date is today.
  const visibleTimeSlots = React.useMemo(() => {
    if (!form.date || form.date !== todayISO) return TIME_SLOTS;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return TIME_SLOTS.filter(slot => {
      const m = slot.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!m) return true;
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const p = m[3].toUpperCase();
      if (p === "PM" && h !== 12) h += 12;
      if (p === "AM" && h === 12) h = 0;
      const slotMin = h * 60 + min;
      // Require at least 5 minutes lead time
      return slotMin >= nowMin + 5;
    });
  }, [form.date, todayISO]);

  const canUsePayPal = isUSD;
  // In the Android app PayPal runs in the system browser instead of the WebView.
  const nativeUsd = isNativeApp() && isUSD;
  const hideInr = isUSD && locked && !nativeUsd;

  const bookingPayload = () => {
    const instant = mode === "instant";
    const chosenConcern = form.concern === "Other" ? form.custom_concern : form.concern;
    return {
      instant,
      date: instant ? new Date().toISOString().split("T")[0] : form.date,
      time_slot: instant ? "Instant · now" : form.time_slot,
      concern: instant ? (chosenConcern || "Instant chat consultation") : chosenConcern,
    };
  };

  // Runs after a payment is confirmed (Cashfree verify or PayPal capture):
  // saves the booking, opens the live-chat room and shows the confirmation.
  const finishBooking = async () => {
    const p = bookingPayload();
    setError(""); setLoading(true);
    try {
      const kundliData = localStorage.getItem("astrofate_report");
      const kundliId = kundliData ? JSON.parse(kundliData)?.report_id : null;
      const bookingRes = await axios.post(`${API}/consultations/book`, {
        date: p.date,
        time_slot: p.time_slot,
        concern: p.concern,
        kundli_report_id: kundliId,
        phone: form.phone,
      }, { withCredentials: true });

      setWaUrls({
        astrologer: bookingRes.data.astrologer_whatsapp_url,
        customer: bookingRes.data.customer_whatsapp_url,
        booking_ref: bookingRes.data.booking_ref,
      });

      try {
        const chatRes = await axios.post(`${API}/livechat/start`, { booking_id: bookingRes.data.id }, { withCredentials: true });
        setChatId(chatRes.data.chat_id);
        if (p.instant && chatRes.data.chat_id) { navigate(`/livechat/${chatRes.data.chat_id}`); return; }
      } catch (chatErr) {
        console.warn("Could not start live chat:", chatErr);
      }
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (err.message || "Booking failed. Please contact support — your payment was received."));
    } finally { setLoading(false); }
  };

  const pp = useNativePayPal(async () => { await finishBooking(); });

  const handleBook = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/auth?redirect=/consultation"); return; }
    if (nativeUsd) {
      return pp.start("consultation", { name: user.name, email: user.email, phone: form.phone });
    }
    setError(""); setLoading(true);
    try {
      // Step 1: Create a Cashfree order for ₹249
      const orderRes = await axios.post(`${API}/payments/create-order`, {
        plan: "consultation",
        name: user.name || form.phone || "Customer",
        email: user.email,
        phone: form.phone,
      }, { withCredentials: true });
      const { order_id, payment_session_id, mock, environment } = orderRes.data;

      // Step 2: Launch real Cashfree checkout (production)
      if (!mock) {
        if (!window.Cashfree) throw new Error("Payment SDK not loaded. Please refresh the page.");
        const cashfree = window.Cashfree({ mode: environment === "production" ? "production" : "sandbox" });
        const result = await cashfree.checkout({ paymentSessionId: payment_session_id, redirectTarget: "_modal" });
        if (result?.error) {
          setError(result.error.message || "Payment was cancelled.");
          setLoading(false);
          return;
        }
      }
      // Step 3: Verify payment with backend (Cashfree-confirmed) before booking
      await axios.post(`${API}/payments/verify/${order_id}`, {}, { withCredentials: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (err.message || "Payment failed. Please try again."));
      setLoading(false);
      return;
    }
    // Step 4 & 5: booking + live chat
    await finishBooking();
  };

  return (
    <div style={{ minHeight: "100vh", background: "#120021", paddingTop: "80px" }}>
      {/* Background */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: "600px", height: "600px", background: "radial-gradient(circle, rgba(91,30,140,0.2), transparent)", top: 0, right: 0, borderRadius: "50%" }} />
        <div style={{ position: "absolute", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(45,11,69,0.3), transparent)", bottom: 0, left: 0, borderRadius: "50%" }} />
      </div>

      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "0 16px 60px", position: "relative", zIndex: 1 }}>
        {/* Header */}
        <div className="animate-fadeInUp" style={{ textAlign: "center", marginBottom: "40px" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>◉</div>
          <h1 className="font-cinzel gradient-text-gold" style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)", marginBottom: "8px" }}>Book Consultation</h1>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.95rem" }}>Connect with an expert Vedic astrologer for personalized guidance</p>
        </div>

        {/* Active consultation banner — shown when the user already has an open session */}
        {showActiveBanner && (
          <div data-testid="active-consultation-banner" className="glass-card animate-fadeInUp" style={{
            padding: "22px 24px",
            marginBottom: "26px",
            border: "1px solid rgba(255,209,102,0.4)",
            background: "linear-gradient(135deg, rgba(255,209,102,0.14), rgba(157,78,221,0.08))",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
              <Star size={16} style={{ color: "#FFD166" }} />
              <span className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.05rem", fontWeight: 700 }}>
                You already have an active consultation
              </span>
            </div>
            <p style={{ color: "rgba(255,255,255,0.78)", fontSize: "0.92rem", lineHeight: 1.55, marginBottom: "6px" }}>
              Booking <strong style={{ color: "#FFD166" }}>#{activeConsult.booking_ref || "—"}</strong>
              {activeConsult.concern && <> &middot; <span style={{ color: "rgba(255,255,255,0.65)" }}>{activeConsult.concern}</span></>}
            </p>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem", marginBottom: "16px" }}>
              {activeConsult.astrologer_first_reply_ts
                ? "Your astrologer is already in the chat — pick up right where you left off."
                : "Your astrologer will join shortly. Open the chat to send them your first message."}
            </p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                data-testid="resume-consultation-btn"
                onClick={() => navigate(`/livechat/${activeConsult.chat_id}`)}
                className="btn-gold"
                style={{ padding: "10px 22px", fontSize: "0.88rem" }}
              >
                Resume Chat →
              </button>
              <button
                data-testid="book-another-toggle-btn"
                type="button"
                onClick={() => setBookAnother(b => !b)}
                className="btn-ghost"
                style={{ padding: "10px 18px", fontSize: "0.82rem" }}
              >
                {bookAnother ? "Hide booking form" : "Book another session"}
              </button>
            </div>
          </div>
        )}

        {/* Pricing card */}
        {showBookingForm && (
        <div className="glass-card animate-fadeInUp delay-100" style={{ padding: "24px", marginBottom: "20px", border: "1px solid rgba(157,78,221,0.2)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px", background: "linear-gradient(135deg, rgba(157,78,221,0.08), rgba(157,78,221,0.02))" }}>
          <div>
            <div style={{ display: "inline-block", background: "linear-gradient(135deg, #ef4444, #f97316)", color: "white", padding: "3px 10px", borderRadius: "50px", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.3px", marginBottom: "8px", textTransform: "uppercase" }}>50% OFF — Limited Time</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
              <div className="font-cinzel" style={{ color: "#9D4EDD", fontSize: "2.1rem", fontWeight: "700" }}>{price("consultation")}</div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "1.15rem", textDecoration: "line-through" }}>{priceOld("consultation")}</div>
            </div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>15-minute live consultation</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {["Personalized Vedic analysis", "Marriage & career guidance", "Remedies & solutions", "Priority report access"].map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", color: "rgba(255,255,255,0.65)", fontSize: "0.85rem" }}>
                <span style={{ color: "#9D4EDD" }}>✓</span> {f}
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Mode toggle — Instant Chat vs Schedule for later */}
        {!success && showBookingForm && (
          <div data-testid="consult-mode-toggle" style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
            <button data-testid="mode-instant-btn" type="button" onClick={() => setMode("instant")}
              style={{ flex: 1, padding: "14px 16px", borderRadius: "14px", cursor: "pointer", textAlign: "left",
                border: `1px solid ${mode === "instant" ? "rgba(255,209,102,0.7)" : "rgba(255,255,255,0.12)"}`,
                background: mode === "instant" ? "linear-gradient(135deg, rgba(255,209,102,0.16), rgba(157,78,221,0.08))" : "rgba(255,255,255,0.02)",
                transition: "all 0.2s" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "8px", color: mode === "instant" ? "#FFD166" : "rgba(255,255,255,0.7)", fontWeight: 700, fontSize: "0.92rem" }}>
                <Zap size={16} /> Instant Chat
              </span>
              <span style={{ display: "block", color: "rgba(255,255,255,0.5)", fontSize: "0.76rem", marginTop: "4px" }}>Start chatting with an astrologer right now — no scheduling.</span>
            </button>
            <button data-testid="mode-scheduled-btn" type="button" onClick={() => setMode("scheduled")}
              style={{ flex: 1, padding: "14px 16px", borderRadius: "14px", cursor: "pointer", textAlign: "left",
                border: `1px solid ${mode === "scheduled" ? "rgba(157,78,221,0.7)" : "rgba(255,255,255,0.12)"}`,
                background: mode === "scheduled" ? "linear-gradient(135deg, rgba(157,78,221,0.16), rgba(157,78,221,0.04))" : "rgba(255,255,255,0.02)",
                transition: "all 0.2s" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "8px", color: mode === "scheduled" ? "#c084fc" : "rgba(255,255,255,0.7)", fontWeight: 700, fontSize: "0.92rem" }}>
                <Calendar size={16} /> Schedule for later
              </span>
              <span style={{ display: "block", color: "rgba(255,255,255,0.5)", fontSize: "0.76rem", marginTop: "4px" }}>Pick a date &amp; time that works best for you.</span>
            </button>
          </div>
        )}

        {/* Instant Chat availability badge — shown before the customer pays */}
        {!success && showBookingForm && mode === "instant" && (
          <div data-testid="astrologer-availability-badge" style={{
            display: "flex", alignItems: "center", gap: "10px", marginBottom: "18px",
            padding: "12px 16px", borderRadius: "12px",
            border: `1px solid ${availability.online ? "rgba(52,211,153,0.35)" : "rgba(255,255,255,0.15)"}`,
            background: availability.online ? "rgba(52,211,153,0.08)" : "rgba(255,255,255,0.03)",
          }}>
            <span style={{ position: "relative", display: "inline-flex" }}>
              <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: availability.online ? "#34D399" : "#9CA3AF", display: "inline-block" }} />
              {availability.online && (
                <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "#34D399", opacity: 0.5, animation: "ping 1.4s cubic-bezier(0,0,0.2,1) infinite" }} />
              )}
            </span>
            <span style={{ color: availability.online ? "#6EE7B7" : "rgba(255,255,255,0.7)", fontSize: "0.9rem", fontWeight: 700 }}>
              {availability.online ? "Astrologer online" : "Astrologer away"}
            </span>
            <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem" }}>
              · {availability.online ? `typically replies in ~${availability.reply_minutes} min` : `usually replies within a few hours (~${availability.reply_minutes} min once online)`}
            </span>
          </div>
        )}

        {/* Consult Now CTA — scheduled mode helper only */}
        {!success && showBookingForm && mode === "scheduled" && (
          <div data-testid="consult-now-banner" className="glass-card" style={{ padding: "16px 20px", marginBottom: "30px", border: "1px solid rgba(255,209,102,0.25)", background: "linear-gradient(135deg, rgba(255,209,102,0.1), rgba(255,209,102,0.02))", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
            <div>
              <p style={{ color: "#FFD166", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
                <Zap size={15} /> Talk to an astrologer right now
              </p>
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem", marginTop: "2px" }}>Auto-fills today&apos;s next available 15-min slot — no waiting.</p>
            </div>
            <button data-testid="consult-now-btn" type="button" onClick={handleConsultNow} className="btn-gold" style={{ padding: "10px 22px", fontSize: "0.88rem" }}>
              Consult Now
            </button>
          </div>
        )}

        {success ? (
          <div className="glass-card animate-fadeInUp" style={{ padding: "48px", textAlign: "center", border: "1px solid rgba(34,197,94,0.2)" }}>
            <div style={{ fontSize: "3rem", marginBottom: "16px" }}>✦</div>
            <h2 className="font-cinzel" style={{ color: "#86efac", fontSize: "1.5rem", marginBottom: "12px" }}>Booking Confirmed!</h2>
            <p style={{ color: "rgba(255,255,255,0.6)", marginBottom: "6px" }}>
              Your consultation is scheduled for <strong style={{ color: "white" }}>{form.date}</strong> at <strong style={{ color: "white" }}>{form.time_slot}</strong>
            </p>
            {waUrls.booking_ref && (
              <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", marginBottom: "20px" }}>
                Booking reference: <strong style={{ color: "#FFD166" }}>#{waUrls.booking_ref}</strong>
              </p>
            )}
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.85rem", marginBottom: "8px" }}>We&apos;ve opened WhatsApp so you can send the booking to the astrologer.</p>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.82rem", marginBottom: "24px" }}>
              📧 Receipt + calendar invite sent to your email
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap", marginBottom: "20px" }}>
              {chatId && (
                <button data-testid="start-live-chat-btn" onClick={() => navigate(`/livechat/${chatId}`)} className="btn-gold" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  ✦ Start Live Chat with Astrologer
                </button>
              )}
              {waUrls.astrologer && (
                <a data-testid="notify-astrologer-whatsapp" href={waUrls.astrologer} target="_blank" rel="noreferrer" className="btn-purple" style={{ textDecoration: "none" }}>
                  Notify on WhatsApp
                </a>
              )}
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button onClick={() => navigate("/dashboard")} className="btn-gold">View Dashboard</button>
              <button onClick={() => navigate("/chat")} className="btn-ghost">Chat with AI</button>
            </div>
          </div>
        ) : showBookingForm ? (
          <div className="glass-card animate-fadeInUp delay-200" style={{ padding: "32px" }}>
            <form onSubmit={handleBook}>
              {/* Phone */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "8px" }}>
                  <MessageCircle size={14} style={{ display: "inline", marginRight: "6px" }} />WhatsApp Number (with country code, e.g. +91...)
                </label>
                <input data-testid="consultation-phone-input" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+919876543210" className="cosmic-input" required />
              </div>
              {/* Date picker */}
              {mode === "scheduled" && (
              <div style={{ marginBottom: "24px" }}>
                <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "8px" }}>
                  <Calendar size={14} style={{ display: "inline", marginRight: "6px" }} />Preferred Date
                </label>
                <input data-testid="consultation-date-input" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} min={minDate} max={maxDate} className="cosmic-input"
                  style={{ colorScheme: "dark" }} required />
              </div>
              )}

              {/* Time slots */}
              {mode === "scheduled" && (
              <div style={{ marginBottom: "24px" }}>
                <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "10px" }}>
                  <Clock size={14} style={{ display: "inline", marginRight: "6px" }} />Preferred Time
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: "8px" }}>
                  {visibleTimeSlots.length === 0 && (
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.82rem", gridColumn: "1/-1", padding: "8px 4px" }}>
                      No more slots available today. Please pick a future date.
                    </div>
                  )}
                  {visibleTimeSlots.map(slot => (
                    <button key={slot} type="button" data-testid={`time-slot-${slot}`} onClick={() => setForm({...form, time_slot: slot})}
                      style={{ padding: "10px", border: `1px solid ${form.time_slot === slot ? "rgba(157,78,221,0.7)" : "rgba(255,255,255,0.1)"}`, borderRadius: "10px", cursor: "pointer",
                        background: form.time_slot === slot ? "rgba(157,78,221,0.2)" : "transparent",
                        color: form.time_slot === slot ? "#c084fc" : "rgba(255,255,255,0.6)", fontSize: "0.82rem", transition: "all 0.2s" }}>
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Concern */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "10px" }}>
                  <MessageCircle size={14} style={{ display: "inline", marginRight: "6px" }} />{mode === "instant" ? "What's on your mind? (optional)" : "Main Concern"}
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
                  {CONCERNS.map(c => (
                    <button key={c} type="button" data-testid={`concern-${c.toLowerCase().replace(/ /g,"-")}`} onClick={() => setForm({...form, concern: c})}
                      style={{ padding: "8px 14px", borderRadius: "50px", cursor: "pointer", border: `1px solid ${form.concern === c ? "rgba(255,209,102,0.6)" : "rgba(255,255,255,0.1)"}`,
                        background: form.concern === c ? "rgba(255,209,102,0.1)" : "transparent",
                        color: form.concern === c ? "#FFD166" : "rgba(255,255,255,0.55)", fontSize: "0.82rem", transition: "all 0.2s" }}>
                      {c}
                    </button>
                  ))}
                </div>
                {form.concern === "Other" && (
                  <textarea value={form.custom_concern} onChange={e => setForm({...form, custom_concern: e.target.value})}
                    placeholder="Describe your specific concern..." className="cosmic-input" rows={3} style={{ resize: "vertical" }} />
                )}
              </div>

              {!user && (
                <div style={{ background: "rgba(255,209,102,0.05)", border: "1px solid rgba(255,209,102,0.2)", borderRadius: "10px", padding: "14px", marginBottom: "20px", textAlign: "center" }}>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.88rem" }}>Please login to book a consultation</p>
                  <button type="button" onClick={() => navigate("/auth?redirect=/consultation")} className="btn-gold" style={{ marginTop: "10px", padding: "8px 20px" }}>Login to Continue</button>
                </div>
              )}

              {(error || pp.error) && <div style={{ color: "#fca5a5", fontSize: "0.85rem", background: "rgba(239,68,68,0.1)", padding: "10px", borderRadius: "8px", marginBottom: "16px" }}>{error || pp.error}</div>}

              {pp.pendingOrder && (
                <div data-testid="consult-paypal-waiting" style={{ color: "#FFD166", fontSize: "0.82rem", background: "rgba(255,209,102,0.08)", padding: "10px 12px", borderRadius: "8px", marginBottom: "16px", lineHeight: 1.55 }}>
                  Complete the PayPal payment in your browser, then come back — your consultation is booked automatically.
                </div>
              )}

              <div style={{ display: "flex", flexDirection: canUsePayPal ? "column-reverse" : "column" }}>
                {!hideInr && (
                  <button data-testid="book-consultation-submit-btn" type="submit"
                    disabled={loading || pp.starting || !user || !form.phone || (mode === "scheduled" && (!form.date || !form.time_slot || !form.concern))}
                    className="btn-purple"
                    style={{ width: "100%", opacity: (loading || pp.starting || !user || !form.phone || (mode === "scheduled" && (!form.date || !form.time_slot || !form.concern))) ? 0.5 : 1, fontSize: "1rem" }}>
                    {loading || pp.starting ? "Processing payment..."
                      : nativeUsd ? `Pay ${price("consultation")} with PayPal`
                      : mode === "instant" ? `Pay ${priceInr("consultation")} & Start Instant Chat`
                      : `Pay ${priceInr("consultation")} & Book Consultation`}
                  </button>
                )}

                {user && canUsePayPal && !nativeUsd && (
                  <>
                    <PayPalCheckout
                      plan="consultation"
                      payer={{ name: user.name, email: user.email, phone: form.phone }}
                      variant="primary"
                      label="Pay"
                      onPaid={() => { finishBooking(); }}
                      onError={(m) => setError(m)}
                    />
                    {!locked && <PayDivider text="or pay in ₹ (UPI / card)" />}
                  </>
                )}
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </div>
  );
}
